import type {
  ComponentPropsWithRef,
  ComponentType,
  ElementType,
  ReactElement,
} from 'react';

import { clsx } from 'clsx';

import { Surface } from '@storebrand-digital/pretzel';
import {
  PlusIcon,
  type BaseIconProps,
} from '@storebrand-digital/pretzel/icons';

import styles from './index.module.css';
import type { DistributiveOmit } from '@bank/utils';

export type ActionCardProps<As extends ElementType = 'button'> = {
  as?: As;
  heading: {
    level: 1 | 2 | 3;
    text: ReactElement | string;
    lookLike?: 1 | 2 | 3;
  };
  icon?: ComponentType<BaseIconProps>;
} & DistributiveOmit<ComponentPropsWithRef<As>, 'as' | 'children'>;

export const ActionCard = <As extends ElementType = 'button'>(
  props: ActionCardProps<As>,
): ReactElement => {
  const {
    as: Comp = 'button',
    heading,
    icon: Icon = PlusIcon,
    className,
    ...rest
  } = props;

  const Heading = `h${heading.level}` as const; // 'h1' | 'h2' | 'h3'

  return (
    <Heading className={clsx(`h${heading.lookLike ?? 2}`, className)}>
      <Comp
        className={clsx(styles.button, 'group')}
        {...(Comp === 'button' ? { type: 'button' } : {})}
        {...rest}
      >
        <Surface
          as="span"
          className={clsx(
            styles.surface,
            'px-16 md:px-20 py-20 md:py-24 rounded-medium grid grid-cols-[1fr_auto] items-center gap-08',
          )}
        >
          <span className="font-body text-primary group-hover:text-primary-hover">
            {heading.text}
          </span>
          <Icon />
        </Surface>
      </Comp>
    </Heading>
  );
};
